import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicId } from "@/hooks/useAppointments";

export interface OverdueVaccineRow {
  planId: string;
  dueDate: Date;
  daysOverdue: number;
  petId: string;
  petName: string;
  ownerId: string;
  ownerName: string;
  itemId: string;
  itemName: string;
  speciesName: string | null;
}

export interface UpcomingApplicationRow {
  planId: string;
  dueDate: Date;
  daysUntilDue: number;
  petId: string;
  petName: string;
  ownerId: string;
  ownerName: string;
  itemId: string;
  itemName: string;
  speciesName: string | null;
}

type ApplicationPlanSelect = {
  id: string;
  due_date: string;
  pet_id: string;
  item_id: string;
  pets: { id: string; name: string; owner_id: string; species: { name: string } | null } | null;
  items: { id: string; name: string } | null;
};

async function fetchOwnerNames(ownerIds: string[]): Promise<Record<string, string>> {
  const ownerNameMap: Record<string, string> = {};
  if (ownerIds.length === 0) return ownerNameMap;

  const { data: owners, error } = await supabase
    .from("owners")
    .select("id, name")
    .in("id", ownerIds);

  if (error) throw error;
  (owners ?? []).forEach((o) => {
    ownerNameMap[o.id] = o.name ?? "Sem nome";
  });

  return ownerNameMap;
}

export function useReportsOverdueVaccines() {
  const { data: clinicId } = useCurrentClinicId();

  return useQuery({
    queryKey: ["reports-overdue-vaccines", clinicId],
    queryFn: async (): Promise<OverdueVaccineRow[]> => {
      if (!clinicId) return [];

      const now = new Date();
      const nowIso = now.toISOString();

      const { data: plans, error } = await supabase
        .from("pet_application_plans")
        .select(
          "id, due_date, pet_id, item_id, pets(id, name, owner_id, species:species_id(name)), items(id, name)"
        )
        .eq("clinic_id", clinicId)
        .not("status", "in", "(completed,canceled)")
        .lt("due_date", nowIso)
        .is("deleted_at", null)
        .order("due_date", { ascending: true })
        .limit(1000);

      if (error) throw error;

      const dayMs = 1000 * 60 * 60 * 24;

      const typedPlans = (plans ?? []) as ApplicationPlanSelect[];

      const ownerIds = [
        ...new Set(typedPlans.map((p) => p.pets?.owner_id).filter(Boolean) as string[]),
      ];
      const ownerNameMap = await fetchOwnerNames(ownerIds);

      return typedPlans
        .map((p) => {
          const dueDate = new Date(p.due_date);
          return {
            planId: p.id,
            dueDate,
            daysOverdue: Math.floor((now.getTime() - dueDate.getTime()) / dayMs),
            petId: p.pet_id,
            petName: p.pets?.name ?? "Pet",
            ownerId: p.pets?.owner_id ?? "",
            ownerName: ownerNameMap[p.pets?.owner_id ?? ""] ?? "Tutor",
            itemId: p.item_id,
            itemName: p.items?.name ?? "Aplicação",
            speciesName: p.pets?.species?.name ?? null,
          };
        })
        .filter((r) => r.ownerId);
    },
    enabled: !!clinicId,
  });
}

export function useReportsUpcomingApplications(daysAhead = 30) {
  const { data: clinicId } = useCurrentClinicId();

  return useQuery({
    queryKey: ["reports-upcoming-applications", clinicId, daysAhead],
    queryFn: async (): Promise<UpcomingApplicationRow[]> => {
      if (!clinicId) return [];

      const now = new Date();
      const end = new Date(now);
      end.setDate(end.getDate() + daysAhead);
      const dayMs = 1000 * 60 * 60 * 24;

      const { data: plans, error } = await supabase
        .from("pet_application_plans")
        .select(
          "id, due_date, pet_id, item_id, pets(id, name, owner_id, species:species_id(name)), items(id, name)"
        )
        .eq("clinic_id", clinicId)
        .not("status", "in", "(completed,canceled)")
        .gte("due_date", now.toISOString())
        .lte("due_date", end.toISOString())
        .is("deleted_at", null)
        .order("due_date", { ascending: true })
        .limit(1000);

      if (error) throw error;

      const typedPlans = (plans ?? []) as ApplicationPlanSelect[];
      const ownerIds = [
        ...new Set(typedPlans.map((p) => p.pets?.owner_id).filter(Boolean) as string[]),
      ];
      const ownerNameMap = await fetchOwnerNames(ownerIds);

      return typedPlans
        .map((p) => {
          const dueDate = new Date(p.due_date);
          return {
            planId: p.id,
            dueDate,
            daysUntilDue: Math.max(0, Math.ceil((dueDate.getTime() - now.getTime()) / dayMs)),
            petId: p.pet_id,
            petName: p.pets?.name ?? "Pet",
            ownerId: p.pets?.owner_id ?? "",
            ownerName: ownerNameMap[p.pets?.owner_id ?? ""] ?? "Tutor",
            itemId: p.item_id,
            itemName: p.items?.name ?? "Aplicação",
            speciesName: p.pets?.species?.name ?? null,
          };
        })
        .filter((r) => r.ownerId);
    },
    enabled: !!clinicId,
  });
}
